import { suppressKeyboardEventForInput } from 'components/AgGrid/utils';
import type {
    WorkOrderActKc2Data,
    WorkOrderStatusEnum,
    ContractStatusEnum,
    WorkOrderPriceData,
    ChapterEnum,
    WorkOrderSeasonData,
} from 'gql/graphql';
import { ActKc2StatusEnum } from 'gql/graphql';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { getActCellClass, useScrolledToActId } from 'pages/Project/Common/hooks/useScrolledToActId';
import { getNumberFormatter } from 'pages/Project/Common/utils';
import { DeviatedColumnTitle } from 'utils/tableCells';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import {
    getRenderWorkOrderPriceKC2CostEdit,
    getRenderWorkOrderPriceKC2Volume,
    getRenderWorkOrderPriceKC2VolumeEdit,
} from '../getRenderWorkOrderPrice';
import { TitleActKC2Columns } from '../TitleActKC2Columns';

interface IUseActKC2ColumnsProps {
    actsKC2Data: WorkOrderActKc2Data[] | undefined | null;
    contractStatus: ContractStatusEnum | undefined;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    isDisabledByProjectStatus: boolean;
    handleOnRowChange: () => void;
    onRefresh: () => void;
    chapter: ChapterEnum;
    workOrderSubObjectId: number;
    seasons: WorkOrderSeasonData[];
    costRounding: number;
}

export const useActKC2Columns = ({
    actsKC2Data,
    contractStatus,
    workOrderStatus,
    isDisabledByProjectStatus,
    handleOnRowChange,
    chapter,
    workOrderSubObjectId,
    onRefresh,
    seasons,
    costRounding,
}: IUseActKC2ColumnsProps) => {
    const { scrolledToActId, withHighLightManager } = useScrolledToActId();

    return useMemo<(ColDef<WorkOrderPriceData> | ColGroupDef<WorkOrderPriceData>)[]>(() => {
        let isOddSeason = true;
        const currentSeasonId = actsKC2Data?.[0]?.seasonId;

        return (actsKC2Data ?? []).map(
            ({
                id,
                seasonId,
                status,
                number,
                date,
                acceptedVolumeDeviation,
                planVolumeDeviation,
                isDistributionOfMtrInflation,
                fullNumber,
                isInactive,
                isCorrection,
                canCreateCorrection,
                correctionDate,
            }) => {
                if (seasonId !== currentSeasonId) {
                    isOddSeason = !isOddSeason;
                }

                const cellClass = getActCellClass(id, isOddSeason, scrolledToActId);

                const inflationSeason: (ColDef<WorkOrderPriceData> | ColGroupDef<WorkOrderPriceData>)[] = [
                    {
                        headerName: 'Сезон инфляции',
                        width: 150,
                        cellRenderer: ({ data }: { data: WorkOrderPriceData }) => {
                            if (!data) {
                                return '';
                            }

                            if (data.hasEmptyActPlanVolume) {
                                return '';
                            }

                            if (data) {
                                const currentDataAct = data.actKC2Prices.find((item) => item.actKC2Id === id);

                                if (currentDataAct?.hasDistributionOfMtrInflation === true) {
                                    if (currentDataAct?.inflationSeasonId === null) {
                                        return 'Не учитывать';
                                    }

                                    const currentSeason = seasons.find(
                                        (item) => item.id === currentDataAct?.inflationSeasonId,
                                    );

                                    return currentSeason?.year;
                                }
                            }

                            return '';
                        },
                        headerClass: `ag-right-aligned-header ${cellClass}`,
                        cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                    },
                ];

                const volumeColAccepted: (ColDef<WorkOrderPriceData> | ColGroupDef<WorkOrderPriceData>)[] = [
                    {
                        field: `actKC2_${id}_accepted` as any,
                        // eslint-disable-next-line react/display-name
                        headerComponent: () => (
                            <div style={{ display: 'flex', width: '100%' }}>
                                <DeviatedColumnTitle
                                    title="Объем (План)"
                                    deviation={status !== ActKc2StatusEnum.Accepted ? planVolumeDeviation : null}
                                    tooltipText="Превышает остаток по сезону"
                                />
                            </div>
                        ),
                        width: 150,
                        minWidth: 150,
                        cellRenderer: ({ data }: { data: WorkOrderPriceData }) => {
                            if (!data) {
                                return '';
                            }

                            if (status === ActKc2StatusEnum.InProgress) {
                                return getRenderWorkOrderPriceKC2VolumeEdit({
                                    key: `actKC2planVolume_${id}`,
                                    id,
                                    status,
                                    onChange: handleOnRowChange,
                                    deviationKey: `actKC2planVolumeDeviation_${id}`,
                                    isReadOnlyKey: `actKC2isReadOnly_${id}`,
                                    contractStatus,
                                    workOrderStatus,
                                    isDisabledByProjectStatus,
                                    row: data,
                                });
                            }

                            return getRenderWorkOrderPriceKC2Volume({
                                key: `actKC2planVolume_${id}`,
                                deviationKey: `actKC2planVolumeDeviation_${id}`,
                                row: data,
                            });
                        },
                        headerClass: `ag-right-aligned-header ${cellClass}`,
                        cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                        suppressKeyboardEvent: suppressKeyboardEventForInput,
                    },
                ];

                const planColAccepted: (ColDef<WorkOrderPriceData> | ColGroupDef<WorkOrderPriceData>)[] = [
                    {
                        headerName: 'Стоимость (План)',
                        width: 150,
                        cellRenderer: ({ data }: { data: WorkOrderPriceData }) => {
                            if (!data) {
                                return '';
                            }

                            const isEditableCost = (data as any)[`actKC2isEditableCost_${id}`];

                            if (status === ActKc2StatusEnum.InProgress && isEditableCost) {
                                return getRenderWorkOrderPriceKC2CostEdit({
                                    key: `actKC2planCost_${id}`,
                                    id,
                                    status,
                                    onChange: handleOnRowChange,
                                    contractStatus,
                                    workOrderStatus,
                                    isDisabledByProjectStatus,
                                    row: data,
                                    decimalScale: costRounding,
                                });
                            }

                            const value = (data as any)[`actKC2planCost_${id}`];

                            return typeof value === 'number' ? getNumberFormatter(costRounding)({ value }) : '';
                        },
                        headerClass: cellClass,
                        cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                        suppressKeyboardEvent: suppressKeyboardEventForInput,
                    },
                ];

                return {
                    seasonId,
                    id,
                    // eslint-disable-next-line react/display-name
                    headerGroupComponent: () => (
                        <TitleActKC2Columns
                            id={id}
                            number={number}
                            status={status}
                            date={date}
                            onRefresh={onRefresh}
                            contractStatus={contractStatus}
                            workOrderStatus={workOrderStatus}
                            isDisabledByProjectStatus={isDisabledByProjectStatus}
                            chapter={chapter}
                            workOrderSubObjectId={workOrderSubObjectId}
                            seasonId={seasonId}
                            fullNumber={fullNumber}
                            isInactive={isInactive}
                            isCorrection={isCorrection}
                            canCreateCorrection={canCreateCorrection}
                            correctionDate={correctionDate}
                            withHighLightManager={withHighLightManager}
                        />
                    ),
                    headerName: formatActTitle(
                        isCorrection ? fullNumber : number,
                        isCorrection ? correctionDate : date,
                    ),
                    marryChildren: true,
                    headerClass: cellClass,
                    children: [
                        ...(isDistributionOfMtrInflation ? inflationSeason : []),
                        ...(status !== ActKc2StatusEnum.Accepted ? volumeColAccepted : []),
                        ...(status !== ActKc2StatusEnum.Accepted ? planColAccepted : []),
                        {
                            colId: id,
                            // eslint-disable-next-line react/display-name
                            headerComponent: () => (
                                <DeviatedColumnTitle
                                    title="Объем (Согласовано)"
                                    deviation={acceptedVolumeDeviation}
                                    tooltipText="Превышает остаток по сезону"
                                />
                            ),
                            width: 155,
                            cellRenderer: ({ data }: { data: WorkOrderPriceData }) => {
                                if (!data) {
                                    return '';
                                }

                                if (status === ActKc2StatusEnum.OnCheck) {
                                    return getRenderWorkOrderPriceKC2VolumeEdit({
                                        key: `actKC2acceptedVolume_${id}`,
                                        id,
                                        status,
                                        onChange: handleOnRowChange,
                                        deviationKey: `actKC2acceptedVolumeDeviation_${id}`,
                                        isReadOnlyKey: `actKC2isReadOnly_${id}`,
                                        contractStatus,
                                        workOrderStatus,
                                        isDisabledByProjectStatus,
                                        row: data,
                                    });
                                }

                                return getRenderWorkOrderPriceKC2Volume({
                                    key: `actKC2acceptedVolume_${id}`,
                                    deviationKey: `actKC2acceptedVolumeDeviation_${id}`,
                                    row: data,
                                });
                            },
                            headerClass: `ag-right-aligned-header ${cellClass}`,
                            cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                            suppressKeyboardEvent: suppressKeyboardEventForInput,
                        },
                        {
                            field: `actKC2_${id}_cost_accepted`,
                            headerName: 'Стоимость (Согласовано)',
                            width: 155,
                            cellRenderer: ({ data }: { data: WorkOrderPriceData }) => {
                                if (!data) {
                                    return '';
                                }

                                const isEditableCost = (data as any)[`actKC2isEditableCost_${id}`];

                                if (status === ActKc2StatusEnum.OnCheck && isEditableCost) {
                                    return getRenderWorkOrderPriceKC2CostEdit({
                                        key: `actKC2acceptedCost_${id}`,
                                        id,
                                        status,
                                        onChange: handleOnRowChange,
                                        contractStatus,
                                        workOrderStatus,
                                        isDisabledByProjectStatus,
                                        row: data,
                                        decimalScale: costRounding,
                                    });
                                }

                                const value = (data as any)[`actKC2acceptedCost_${id}`];

                                return typeof value === 'number' ? getNumberFormatter(costRounding)({ value }) : '';
                            },
                            headerClass: cellClass,
                            cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                            suppressKeyboardEvent: suppressKeyboardEventForInput,
                        },
                    ],
                };
            },
        );
    }, [
        actsKC2Data,
        chapter,
        contractStatus,
        costRounding,
        handleOnRowChange,
        isDisabledByProjectStatus,
        onRefresh,
        scrolledToActId,
        seasons,
        withHighLightManager,
        workOrderStatus,
        workOrderSubObjectId,
    ]);
};
